import React, { useState, useEffect } from "react";
import { Grid, Box } from "@mui/material";
import { getAllClasses, createClass, updateClass, deleteClass } from "./api";
import { ClassData, ApiResponse, CreateClassParams } from "./interface";
import { getErrorMessage } from "../../utils/error";
import GridTable from "../../components/GridTable";
import ActionMenu from "../../components/ActionMenu";
import Dialog from "../../components/Dialog";
import Button from "../../components/Button";
import TextField from "../../components/TextField";
import { GridCellParams } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import YardOutlined from "@mui/icons-material/YardOutlined";
import Loader from "../../components/Loader";
import { successMessage, errorMessage } from "../../components/Toaster";
import SearchIcon from "@mui/icons-material/Search";
import { HexColorPicker } from "react-colorful";
import { Formik, Form } from "formik";
import * as Yup from "yup";

const validationSchema = Yup.object().shape({
    className: Yup.string().required("Class Name is required"),
    classCode: Yup.string().required("Class Code is required"),
    colorCode: Yup.string().required("Color Code is required"),
    classTranslation: Yup.object()
        .shape({
            EN: Yup.string().required("English Translation is required"),
            GU: Yup.string().required("Gujarati Translation is required"),
            HI: Yup.string().required("Hindi Translation is required"),
        })
        .required("Translations are required"),
});

const Class: React.FC = () => {
    const [classes, setClasses] = useState<ClassData[]>([]);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const [editClass, setEditClass] = useState<ClassData | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState({
        update: false,
        delete: false,
        create: false,
        fetch: false,
    });

    const columns = [
        {
            field: "class_name",
            headerName: "Class Name",
            headerClassName: "table-header",
            flex: 1,
            disableColumnMenu: true,
        },
        {
            field: "class_code",
            headerName: "Class Code",
            headerClassName: "table-header",
            flex: 1,
        },
        {
            field: "color_code",
            headerName: "Color Code",
            headerClassName: "table-header",
            flex: 1,
            renderCell: (params: GridCellParams) => (
                <Button
                    style={{
                        backgroundColor: params.value as string,
                        width: 20,
                        height: 20,
                    }}
                ></Button>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            headerClassName: "table-header",
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <EditIcon color="primary" />,
                            tooltip: "Edit",
                            onClick: handleEdit,
                        },
                        {
                            icon: <DeleteIcon color="error" />,
                            tooltip: "Delete",
                            onClick: handleDelete,
                        }
                    ]}
                />
            ),
            sortable: false,
            filterable: false,
            flex: 1,
        },
    ];


    useEffect(() => {
        const fetchClasses = async () => {
            setLoading(prev => ({ ...prev, fetch: true }));
            try {
                const res: ApiResponse<ClassData[]> = await getAllClasses();
                if (res.status && res.data) {
                    setClasses(res.data);
                } else {
                    errorMessage(res.message || "Failed to fetch classes.");
                }
            } catch (error) {
                errorMessage(getErrorMessage(error) || "Error fetching classes");
            } finally {
                setLoading(prev => ({ ...prev, fetch: false }));
            }
        };

        fetchClasses();
    }, []);


    const openDialog = () => setDialogOpen(true);
    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setDialogOpen(false);
        setEditClass(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setItemToDelete(null);
    };

    const handleEdit = (params: GridCellParams) => {
        setEditClass(params.row as ClassData);
        openDialog();
    };

    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteDialogOpen(true);
    };

    const confirmDialog = async (
        values: any,
        { resetForm }: { resetForm: () => void }
    ) => {
        let success = false;
        if (editClass) {
            success = await handleUpdate(values);
        } else {
            success = await handleCreate(values);
        }
        if (success) {
            closeDialog(resetForm);
        }
    };

    const handleCreate = async (values: any) => {
        setLoading(prev => ({ ...prev, create: true }));
        try {
            const newClassData: CreateClassParams = {
                class_code: values.classCode.trim(),
                color_code: values.colorCode.trim(),
                class_name: values.className.trim(),
                class_translation: values.classTranslation,
            };
            const response = await createClass(newClassData);
            if (response.status) {
                const newClass = {
                    class_id: response.data!.class_id,
                    class_code: values.classCode,
                    color_code: values.colorCode,
                    class_name: values.className,
                    class_translation: values.classTranslation
                };
                setClasses(prev => [...prev, newClass]);
                successMessage(response.message || 'Class created successfully!');
                return true;
            } else {
                errorMessage(response.message || 'Failed to create class.');
                return false;
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || 'Unexpected error occurred.');
            return false;
        } finally {
            setLoading(prev => ({ ...prev, create: false }));
        }
    };

    const handleUpdate = async (values: any) => {
        setLoading(prev => ({ ...prev, update: true }));
        try {
            const updatedClassData = {
                class_code: values.classCode.trim(),
                color_code: values.colorCode.trim(),
                class_name: values.className.trim(),
                class_translation: values.classTranslation,
                class_id: editClass?.class_id || "",
            };
            const response = await updateClass(updatedClassData);
            if (response.status) {
                setClasses(prevClasses =>
                    prevClasses.map(cls =>
                        cls.class_id === editClass!.class_id
                            ? { ...cls, ...updatedClassData }
                            : cls
                    )
                );
                successMessage(response.message || 'Class updated successfully!');
                return true;
            } else {
                errorMessage(response.message || 'Failed to update class. Please try again.');
                return false;
            }
        } catch (error) {
            console.error('Error updating class:', error);
            const message = getErrorMessage(error);
            errorMessage(message || 'An unexpected error occurred. Please try again.');
            return false;
        } finally {
            setLoading(prev => ({ ...prev, update: false }));
        }
    };


    const handleDeleteConfirm = async () => {
        setLoading(prev => ({ ...prev, delete: true }));
        try {
            if (itemToDelete) {
                const response = await deleteClass(itemToDelete.row.class_id);
                if (response.status) {
                    setClasses(prev => prev.filter(cls => cls.class_id !== itemToDelete.row.class_id));
                    successMessage(response.message);
                } else {
                    errorMessage(response.message || 'Failed to delete class.');
                }
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || 'Unexpected error occurred.');
        } finally {
            setLoading(prev => ({ ...prev, delete: false }));
            setDeleteDialogOpen(false);
        }
    };


    return (
        <>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box>
                    <main className="m-a-20">
                        <div className="d-flex justify-end">
                            <div className="d-flex justify-between w-100">
                                <TextField
                                    className="w-25"
                                    label="Search"
                                    variant="outlined"
                                    value={searchQuery}
                                    size="small"
                                    startIcon={<SearchIcon />}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <Button
                                    label="Add"
                                    variant="contained"
                                    color="primary"
                                    onClick={openDialog}
                                />
                            </div>
                        </div>
                        <div className="ag-theme-alpine m-t-20" style={{ height: '100%', width: '100%' }}>
                            <GridTable
                                rows={classes}
                                columns={columns}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                disableRowSelectionOnClick
                                getRowId={(row: ClassData) => row.class_id}
                                density="standard"
                            />
                        </div>
                        <Formik
                            initialValues={{
                                className: editClass ? editClass.class_name : "",
                                classCode: editClass ? editClass.class_code : "",
                                colorCode: editClass ? editClass.color_code : "#aabbcc",
                                classTranslation: editClass
                                    ? editClass.class_translation
                                    : { EN: "", GU: "", HI: "" },
                            }}
                            validationSchema={validationSchema}
                            onSubmit={confirmDialog}
                            enableReinitialize
                        >
                            {({ values, errors, touched, handleChange, handleSubmit, resetForm }) => (
                                <Dialog
                                    open={isDialogOpen}
                                    onClose={() => closeDialog(resetForm)}
                                    onConfirm={handleSubmit}
                                    title={
                                        !loading.update && !loading.create
                                            ? editClass
                                                ? "Edit Class"
                                                : "Add New Class"
                                            : ""
                                    }
                                    confirmButtonText={
                                        !loading.update && !loading.create
                                            ? editClass
                                                ? "Update"
                                                : "Add"
                                            : ""
                                    }
                                    cancelButtonText={
                                        !loading.update && !loading.create ? "Cancel" : ""
                                    }
                                    icon={<YardOutlined />}
                                    message={
                                        <Box padding={4}>
                                            {loading.update || loading.create ? (
                                                <Loader color="primary" size={40} />
                                            ) : (
                                                <Form>
                                                    <Grid container spacing={2}>
                                                        <Grid size={4}>
                                                            <TextField
                                                                name="className"
                                                                label="Class Name"
                                                                value={values.className}
                                                                onChange={handleChange}
                                                                error={Boolean(
                                                                    touched.className && errors.className
                                                                )}
                                                                helperText={
                                                                    touched.className && errors.className
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid size={4}>
                                                            <TextField
                                                                name="classCode"
                                                                label="Class Code"
                                                                value={values.classCode}
                                                                onChange={handleChange}
                                                                error={Boolean(
                                                                    touched.classCode && errors.classCode
                                                                )}
                                                                helperText={
                                                                    touched.classCode && errors.classCode
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid size={4}>
                                                            <Box>
                                                                <HexColorPicker
                                                                    color={values.colorCode}
                                                                    onChange={(color) => {
                                                                        handleChange({
                                                                            target: {
                                                                                name: "colorCode",
                                                                                value: color,
                                                                            },
                                                                        });
                                                                    }}
                                                                />
                                                                <br />
                                                                <h3>Selected Color: {values.colorCode}</h3>
                                                            </Box>
                                                        </Grid>
                                                        <Grid size={4}>
                                                            <TextField
                                                                name="classTranslation.EN"
                                                                label="English Translation"
                                                                value={values.classTranslation.EN}
                                                                onChange={handleChange}
                                                                error={Boolean(
                                                                    touched.classTranslation?.EN &&
                                                                    errors.classTranslation?.EN
                                                                )}
                                                                helperText={
                                                                    touched.classTranslation?.EN &&
                                                                    errors.classTranslation?.EN
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid size={4}>
                                                            <TextField
                                                                name="classTranslation.GU"
                                                                label="Gujarati Translation"
                                                                value={values.classTranslation.GU}
                                                                onChange={handleChange}
                                                                error={Boolean(
                                                                    touched.classTranslation?.GU &&
                                                                    errors.classTranslation?.GU
                                                                )}
                                                                helperText={
                                                                    touched.classTranslation?.GU &&
                                                                    errors.classTranslation?.GU
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid size={4}>
                                                            <TextField
                                                                name="classTranslation.HI"
                                                                label="Hindi Translation"
                                                                value={values.classTranslation.HI}
                                                                onChange={handleChange}
                                                                error={Boolean(
                                                                    touched.classTranslation?.HI &&
                                                                    errors.classTranslation?.HI
                                                                )}
                                                                helperText={
                                                                    touched.classTranslation?.HI &&
                                                                    errors.classTranslation?.HI
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Form>
                                            )}
                                        </Box>
                                    }
                                />
                            )}
                        </Formik>
                    </main>
                </Box>
            )}
            <Dialog
                open={isDeleteDialogOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title={!loading.delete ? "Delete Class" : ""}
                confirmButtonText={!loading.delete ? "Confirm" : ""}
                cancelButtonText={!loading.delete ? "Cancel" : ""}
                icon={<DeleteIcon />}
                message={
                    <Box padding={2}>
                        {loading.delete ? (
                            <Loader color="primary" size={40} />
                        ) : (
                            "Are you sure you want to delete this class?"
                        )}
                    </Box>
                }
            />
        </>
    );
};

export default Class;
